import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, LabelList, ResponsiveContainer, Cell,
} from 'recharts';
import { getGPS, getPlayers } from '../../api';
import { useSeason, dateToSeason } from '../../SeasonContext';
import { useUrlParam } from '../../hooks/useUrlParam';

// ── ISO week helpers (same as PlayerChronic) ───────────────────────────────────
function getISOWeek(dateStr) {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
}
function getISOYear(dateStr) {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  return d.getFullYear();
}
function isoKey(dateStr) {
  return `${getISOYear(dateStr)}-${String(getISOWeek(dateStr)).padStart(2, '0')}`;
}
function fmtDate(dateStr) {
  if (!dateStr) return '';
  const [, m, d] = dateStr.split('-');
  return `${d}/${m}`;
}

// ── Config ────────────────────────────────────────────────────────────────────
const POSITIONS = ['All', 'CB', 'FB', 'CDM', 'CAM', 'ST', 'W'];
const RANGE_OPTIONS = [4, 8, 12, 16];
const METRICS = [
  { key: 'hmld', label: 'HMLD' },
  { key: 'td',   label: 'Total Dist.' },
  { key: 'hid',  label: 'HID' },
  { key: 'expl', label: 'Explosive Dist.' },
  { key: 'rd',   label: 'Run Dist. >14.4' },
];

const fmt0 = n => (n == null || isNaN(n) ? '—' : Math.round(n).toLocaleString());
const fmtK = n => {
  if (n == null || isNaN(n)) return '—';
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : Math.round(n).toString();
};

// ── Custom bar label ──────────────────────────────────────────────────────────
function BarTopLabel({ x, y, width, value }) {
  if (!value) return null;
  return (
    <text x={x + width / 2} y={y - 5} textAnchor="middle"
      fontSize={9} fill="var(--text-muted)" fontFamily="Inter, sans-serif">
      {fmtK(value)}
    </text>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function TeamWeek() {
  const { selectedSeasons } = useSeason();
  const [rawSessions, setRawSessions] = useState([]);
  const [players, setPlayers]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [rangeWeeksRaw, setRangeWeeks] = useUrlParam('weeks', '8');
  const rangeWeeks = Number(rangeWeeksRaw) || 8;
  const [posFilter, setPosFilter]     = useUrlParam('position', 'All');
  const [metricKey, setMetricKey]     = useState('hmld');
  const [selectedKey, setSelectedKey] = useState(null);

  useEffect(() => {
    Promise.all([getGPS(), getPlayers()])
      .then(([d, p]) => { setRawSessions(d || []); setPlayers(p || []); })
      .finally(() => setLoading(false));
  }, []);

  // ── Per-session team averages (filtered by position) ─────────────────────────
  const processedSessions = useMemo(() => {
    return rawSessions
      .filter(s => s.session_date)
      .filter(s => {
        const season = dateToSeason(s.session_date.slice(0, 10));
        return !season || selectedSeasons.length === 0 || selectedSeasons.includes(season);
      })
      .map(s => {
        const all  = s.players || [];
        const rows = posFilter === 'All'
          ? all
          : all.filter(p => p.position === posFilter);
        if (!rows.length) return null;

        const avg = key => rows.reduce((sum, p) => sum + (Number(p[key]) || 0), 0) / rows.length;
        const distinctMdLabels = [...new Set(rows.map(p => p.md_label).filter(Boolean))];

        return {
          id:          s.id,
          date:        s.session_date.slice(0, 10),
          title:       s.session_title || '',
          sessionType: s.session_type || 'training',
          mdLabel:     distinctMdLabels.join(', ') || '—',
          playerCount: rows.length,
          weekKey:     isoKey(s.session_date.slice(0, 10)),
          td:   avg('td'),
          rd:   avg('rd'),
          hid:  avg('hid'),
          sd:   avg('sd'),
          sa:   avg('sa'),
          expl: avg('expl'),
          hmld: avg('hmld'),
          // Keep raw rows for per-player lookups
          _rows: rows,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [rawSessions, posFilter, selectedSeasons]);

  // ── Group by ISO week ─────────────────────────────────────────────────────────
  const allWeeks = useMemo(() => {
    const map = new Map();
    for (const s of processedSessions) {
      if (!map.has(s.weekKey)) {
        map.set(s.weekKey, {
          key: s.weekKey,
          weekNum: getISOWeek(s.date),
          year:    getISOYear(s.date),
          hasMatch: false,
          sessions: [],
        });
      }
      const wk = map.get(s.weekKey);
      wk.sessions.push(s);
      if (s.sessionType === 'match') wk.hasMatch = true;
    }
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [processedSessions]);

  // ── Slice to last N weeks ─────────────────────────────────────────────────────
  const visibleWeeks = useMemo(() => allWeeks.slice(-rangeWeeks), [allWeeks, rangeWeeks]);

  // ── Auto-select the most recent week ─────────────────────────────────────────
  useEffect(() => {
    if (visibleWeeks.length) {
      setSelectedKey(prev =>
        visibleWeeks.find(w => w.key === prev) ? prev : visibleWeeks[visibleWeeks.length - 1].key
      );
    }
  }, [visibleWeeks]);

  // ── Chart data ────────────────────────────────────────────────────────────────
  const chartData = useMemo(() => visibleWeeks.map(wk => ({
    weekKey:  wk.key,
    label:    `W${wk.weekNum}`,
    subLabel: `'${String(wk.year).slice(2)}`,
    value:    wk.sessions.reduce((sum, s) => sum + (s[metricKey] || 0), 0),
    hasMatch: wk.hasMatch,
  })), [visibleWeeks, metricKey]);

  const avgLine = useMemo(() => {
    if (!chartData.length) return 0;
    return chartData.reduce((s, d) => s + d.value, 0) / chartData.length;
  }, [chartData]);

  // ── Selected week data ────────────────────────────────────────────────────────
  const selectedWeek = useMemo(
    () => visibleWeeks.find(w => w.key === selectedKey) || null,
    [visibleWeeks, selectedKey]
  );

  const summaryCards = useMemo(() => {
    if (!selectedWeek) return null;
    const sess = selectedWeek.sessions;
    const matchSess    = sess.filter(s => s.sessionType === 'match').length;
    const trainingSess = sess.length - matchSess;
    const avgHmld      = sess.length ? sess.reduce((s, x) => s + x.hmld, 0) / sess.length : 0;

    // Highest HMLD from any individual player in any session this week
    let topHmld = 0, topName = '—';
    for (const s of sess) {
      for (const p of s._rows) {
        const v = Number(p.hmld) || 0;
        if (v > topHmld) {
          topHmld = v;
          // Resolve via players.name rather than the stored gps_data.player_name
          // text, which may be stale historical data.
          topName = players.find(pl => pl.id === p.player_id)?.name || p.player_name || p.name || '—';
        }
      }
    }
    return { totalSess: sess.length, matchSess, trainingSess, avgHmld, topHmld, topName };
  }, [selectedWeek, players]);

  const handleBarClick = useCallback((weekKey) => {
    if (weekKey) setSelectedKey(weekKey);
  }, []);

  // ── Week totals for the sessions table's footer row (sum of per-session
  // averages, matching the bar chart's own "weekly total" convention) ──
  const weekTotals = useMemo(() => {
    if (!selectedWeek) return null;
    const sums = { td: 0, rd: 0, hid: 0, sd: 0, sa: 0, hmld: 0, expl: 0 };
    selectedWeek.sessions.forEach(s => {
      Object.keys(sums).forEach(k => { sums[k] += s[k] || 0; });
    });
    return sums;
  }, [selectedWeek]);

  // Training days = all session days in the week, matches included, used to
  // spread the week total across the days actually trained/played.
  const weekTrainingDays = useMemo(() => {
    if (!selectedWeek) return 0;
    return selectedWeek.sessions.length;
  }, [selectedWeek]);

  const weekTotalsPerTrainingDay = useMemo(() => {
    if (!weekTotals || !weekTrainingDays) return null;
    return Object.fromEntries(Object.entries(weekTotals).map(([k, v]) => [k, v / weekTrainingDays]));
  }, [weekTotals, weekTrainingDays]);

  // ── Shared styles ─────────────────────────────────────────────────────────────
  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 };
  const SEL  = {
    padding: '6px 26px 6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)',
    background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.83rem',
    fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
    appearance: 'none', WebkitAppearance: 'none',
  };
  const TH = {
    padding: '8px 10px', fontSize: '0.66rem', fontWeight: 600, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: 'var(--text-muted)', textAlign: 'right',
    borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-app)',
  };
  const THL = { ...TH, textAlign: 'left' };
  const TD_ = { padding: '8px 10px', fontSize: '0.81rem', color: 'var(--text-primary)', textAlign: 'right', borderBottom: '1px solid var(--border)' };
  const TDL = { ...TD_, textAlign: 'left' };

  const metricLabel = METRICS.find(m => m.key === metricKey)?.label || metricKey;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
      Loading…
    </div>
  );

  const hasData = processedSessions.length > 0;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Team Week</h2>
        <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>Weekly team load overview</p>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Range */}
        <div style={{ position: 'relative' }}>
          <select value={rangeWeeks} onChange={e => setRangeWeeks(Number(e.target.value))} style={SEL}>
            {RANGE_OPTIONS.map(n => <option key={n} value={n}>Last {n} weeks</option>)}
          </select>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>

        {/* Position */}
        <div style={{ display: 'flex', gap: 4 }}>
          {POSITIONS.map(pos => (
            <button key={pos} onClick={() => setPosFilter(pos)}
              style={{
                padding: '5px 10px', borderRadius: 6, fontSize: '0.78rem', fontFamily: 'inherit',
                fontWeight: posFilter === pos ? 600 : 400,
                border: posFilter === pos ? '1px solid #3b82f6' : '1px solid var(--border)',
                background: posFilter === pos ? '#3b82f6' : 'var(--bg-card)',
                color: posFilter === pos ? '#fff' : 'var(--text-secondary)',
                cursor: 'pointer',
              }}>
              {pos}
            </button>
          ))}
        </div>

        {/* Metric */}
        <div style={{ marginLeft: 'auto', position: 'relative' }}>
          <select value={metricKey} onChange={e => setMetricKey(e.target.value)} style={SEL}>
            {METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {/* ── Empty state ── */}
      {!hasData && (
        <div style={{ ...card, padding: 48, textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            No GPS data found. Upload sessions in the Upload zone first.
          </p>
        </div>
      )}

      {hasData && visibleWeeks.length === 0 && (
        <div style={{ ...card, padding: 48, textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            No data in the selected range and position filter.
          </p>
        </div>
      )}

      {hasData && visibleWeeks.length > 0 && (
        <>
          {/* ── Main bar chart ── */}
          <div style={{ ...card, padding: '20px 20px 12px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <span style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                Weekly {metricLabel}
              </span>
              <div style={{ display: 'flex', gap: 16, fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: '#1d4ed8', display: 'inline-block' }} />
                  Selected week
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: '#93c5fd', display: 'inline-block' }} />
                  Match week
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: '#bfdbfe', display: 'inline-block' }} />
                  Training week
                </span>
              </div>
            </div>

            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData} margin={{ top: 18, right: 8, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="label"
                    tick={({ x, y, payload, index }) => (
                      <g transform={`translate(${x},${y + 5})`}>
                        <text textAnchor="middle" fill={chartData[index]?.weekKey === selectedKey ? '#60a5fa' : 'var(--text-secondary)'}
                          fontSize={10} fontWeight={chartData[index]?.weekKey === selectedKey ? 700 : 400}
                          fontFamily="Inter, sans-serif" dy={0}>{payload.value}</text>
                        <text textAnchor="middle" fill="var(--text-dimmed)" fontSize={8}
                          fontFamily="Inter, sans-serif" dy={11}>{chartData[index]?.subLabel}</text>
                      </g>
                    )}
                    axisLine={false} tickLine={false} interval={0} height={30} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
                    axisLine={false} tickLine={false}
                    tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v} />
                  <Tooltip
                    cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                    contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: 'var(--text-primary)', fontWeight: 600 }}
                    formatter={(v, _) => [fmt0(v), metricLabel]}
                    labelFormatter={(_, payload) => payload?.[0]?.payload
                      ? `Week ${getISOWeek(visibleWeeks.find(w => w.key === payload[0].payload.weekKey)?.sessions?.[0]?.date || '') || payload[0].payload.label.replace('W','')}`
                      : payload?.[0]?.payload?.label || ''}
                  />
                  <ReferenceLine y={avgLine} stroke="#f59e0b" strokeDasharray="5 3" strokeWidth={1.5}
                    label={{ value: `Avg ${fmtK(avgLine)}`, position: 'insideTopRight', fill: '#f59e0b', fontSize: 9, fontFamily: 'Inter, sans-serif' }} />
                  <Bar dataKey="value" maxBarSize={52} radius={[3, 3, 0, 0]}
                    style={{ cursor: 'pointer' }}
                    onClick={(barDatum) => handleBarClick(barDatum?.weekKey ?? barDatum?.payload?.weekKey)}>
                    <LabelList content={BarTopLabel} />
                    {chartData.map((d, i) => (
                      <Cell key={i}
                        fill={d.weekKey === selectedKey
                          ? '#1d4ed8'
                          : d.hasMatch ? '#93c5fd' : '#bfdbfe'}
                        fillOpacity={1}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ── Summary cards ── */}
          {summaryCards && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                {
                  label: 'Sessions this week',
                  value: summaryCards.totalSess,
                  sub: `Week ${selectedWeek?.weekNum}`,
                },
                {
                  label: 'Match vs Training days',
                  value: `${summaryCards.matchSess}M / ${summaryCards.trainingSess}T`,
                  sub: summaryCards.matchSess > 0 ? 'Match week' : 'Training week',
                  accent: summaryCards.matchSess > 0,
                },
                {
                  label: 'Avg HMLD / player / session',
                  value: fmt0(summaryCards.avgHmld),
                  sub: 'metres',
                },
                {
                  label: 'Top HMLD this week',
                  value: fmt0(summaryCards.topHmld),
                  sub: summaryCards.topName,
                },
              ].map(({ label, value, sub, accent }) => (
                <div key={label} style={{ ...card, padding: '16px 18px' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.3 }}>{label}</div>
                  <div style={{ fontSize: '1.35rem', fontWeight: 700, color: accent ? '#60a5fa' : 'var(--text-primary)', lineHeight: 1 }}>{value}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-dimmed)', marginTop: 5 }}>{sub}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── Session breakdown table ── */}
          {selectedWeek && (
            <div style={card}>
              <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  Sessions — Week {selectedWeek.weekNum}, {selectedWeek.year}
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {selectedWeek.sessions.length} session{selectedWeek.sessions.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                  <thead>
                    <tr>
                      <th style={THL}>Date</th>
                      <th style={THL}>Session</th>
                      <th style={TH}>MD Label</th>
                      <th style={TH}>Players</th>
                      <th style={TH}>Avg TD</th>
                      <th style={TH}>Avg RD</th>
                      <th style={TH}>Avg HID</th>
                      <th style={TH}>Avg Sprint</th>
                      <th style={TH}>Avg SA</th>
                      <th style={TH}>Avg HMLD</th>
                      <th style={TH}>Avg Expl</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedWeek.sessions.map((s, i) => (
                      <tr key={s.id} style={{ background: i % 2 !== 0 ? 'var(--bg-row-alt)' : 'transparent' }}>
                        <td style={{ ...TDL, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(s.date)}</td>
                        <td style={TDL}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            {s.sessionType === 'match' && (
                              <span style={{ fontSize: '0.66rem', fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: 'rgba(37,99,235,0.2)', color: '#93c5fd', whiteSpace: 'nowrap' }}>
                                MATCH
                              </span>
                            )}
                            <span style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-primary)' }}>{s.title}</span>
                          </div>
                        </td>
                        <td style={{ ...TD_, color: 'var(--text-muted)', fontSize: '0.78rem' }}>{s.mdLabel}</td>
                        <td style={TD_}>{s.playerCount}</td>
                        <td style={TD_}>{fmt0(s.td)}</td>
                        <td style={TD_}>{fmt0(s.rd)}</td>
                        <td style={TD_}>{fmt0(s.hid)}</td>
                        <td style={TD_}>{fmt0(s.sd)}</td>
                        <td style={TD_}>{fmt0(s.sa)}</td>
                        <td style={{ ...TD_, fontWeight: 600 }}>{fmt0(s.hmld)}</td>
                        <td style={TD_}>{fmt0(s.expl)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {weekTotals && (
                    <tfoot>
                      <tr style={{ background: 'var(--bg-app)', borderTop: '2px solid var(--border-strong)' }}>
                        <td colSpan={4} style={{ padding: '10px 10px', fontSize: '0.81rem', fontWeight: 700, color: 'var(--text-secondary)' }}>Week total</td>
                        <td style={{ ...TD_, fontWeight: 700 }}>{fmt0(weekTotals.td)}</td>
                        <td style={{ ...TD_, fontWeight: 700 }}>{fmt0(weekTotals.rd)}</td>
                        <td style={{ ...TD_, fontWeight: 700 }}>{fmt0(weekTotals.hid)}</td>
                        <td style={{ ...TD_, fontWeight: 700 }}>{fmt0(weekTotals.sd)}</td>
                        <td style={{ ...TD_, fontWeight: 700 }}>{fmt0(weekTotals.sa)}</td>
                        <td style={{ ...TD_, fontWeight: 700 }}>{fmt0(weekTotals.hmld)}</td>
                        <td style={{ ...TD_, fontWeight: 700 }}>{fmt0(weekTotals.expl)}</td>
                      </tr>
                      <tr style={{ background: 'var(--bg-app)' }}>
                        <td colSpan={4} style={{ padding: '10px 10px', fontSize: '0.81rem', fontWeight: 600, color: 'var(--text-muted)' }}>
                          Totals / training days{weekTrainingDays ? ` (${weekTrainingDays})` : ''}
                        </td>
                        <td style={{ ...TD_, color: 'var(--text-secondary)' }}>{weekTotalsPerTrainingDay ? fmt0(weekTotalsPerTrainingDay.td) : '—'}</td>
                        <td style={{ ...TD_, color: 'var(--text-secondary)' }}>{weekTotalsPerTrainingDay ? fmt0(weekTotalsPerTrainingDay.rd) : '—'}</td>
                        <td style={{ ...TD_, color: 'var(--text-secondary)' }}>{weekTotalsPerTrainingDay ? fmt0(weekTotalsPerTrainingDay.hid) : '—'}</td>
                        <td style={{ ...TD_, color: 'var(--text-secondary)' }}>{weekTotalsPerTrainingDay ? fmt0(weekTotalsPerTrainingDay.sd) : '—'}</td>
                        <td style={{ ...TD_, color: 'var(--text-secondary)' }}>{weekTotalsPerTrainingDay ? fmt0(weekTotalsPerTrainingDay.sa) : '—'}</td>
                        <td style={{ ...TD_, color: 'var(--text-secondary)' }}>{weekTotalsPerTrainingDay ? fmt0(weekTotalsPerTrainingDay.hmld) : '—'}</td>
                        <td style={{ ...TD_, color: 'var(--text-secondary)' }}>{weekTotalsPerTrainingDay ? fmt0(weekTotalsPerTrainingDay.expl) : '—'}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
